import * as React from 'react'
import styled from 'styled-components'

import * as ds from '@bob/design-system'
import * as entity from '@boutique/entities'
import * as boutique from '@boutique/contentful'

import { useInfiniteCarousel } from '@bob/hooks'

import { Controls, RotativeCard, Tile } from '@bob/components'

export type CriteriaSliderProps = {
  activeIndex?: number
  backgroundColor: 'PINK' | 'YELLOW'
  className?: string
  setActiveIndex: React.Dispatch<React.SetStateAction<number | undefined>>
  items: entity.tag.ManifestoTag[]
}

function CriteriaSliderComponent({
  activeIndex,
  backgroundColor,
  className,
  setActiveIndex,
  items,
}: CriteriaSliderProps): React.ReactElement<CriteriaSliderProps> {
  const data = React.useMemo(
    () =>
      items
        .map((item) => ({
          imgSrc: item.page?.imageCover.src,
          title: item.name,
          svg: item.pillar?.icon,
        }))
        .filter(
          (item): item is { imgSrc: string; title: string; svg: boutique.PillarIcon } =>
            item.imgSrc !== undefined && item.svg !== undefined,
        ),
    [items],
  )

  const { ref, renderedList, onClickElement, onScroll, onScrollOneStepLeft, onScrollOneStepRight } =
    useInfiniteCarousel({
      data,
      activeIndex,
      elementWidth: 255,
      setActiveIndex,
    })

  return (
    <CarouselWrapper className={className} backgroundColor={backgroundColor}>
      <HidingWrapper>
        <HidingBlock />
        <HidingBlock />
      </HidingWrapper>
      <RotativeCardWrapper>
        <RotativeCard activeIndex={activeIndex} data={data} />
      </RotativeCardWrapper>

      <InfiniteList ref={ref} onScroll={onScroll}>
        {renderedList.map((criterion, index) => (
          <Tile
            key={index + '-' + criterion.title}
            isFocused={activeIndex === criterion.realIndex}
            onFocus={() => onClickElement(index, criterion.realIndex)}
            title={criterion.title}
            svg={criterion.svg}
          />
        ))}
      </InfiniteList>
      <ControlsWrapper>
        <ds.CircularButton outlined onClick={onScrollOneStepLeft}>
          <ds.icons.ChevronLeft size={32} />
        </ds.CircularButton>
        <ds.CircularButton outlined onClick={onScrollOneStepRight}>
          <ds.icons.ChevronRight size={32} />
        </ds.CircularButton>
        <Controls.HalfTransparentBlock color={ds.color.YELLOW} position="left" />
        <Controls.HalfTransparentBlock color={ds.color.YELLOW} position="right" />
      </ControlsWrapper>
    </CarouselWrapper>
  )
}

const CarouselWrapper = styled.div<{ backgroundColor: 'PINK' | 'YELLOW' }>`
  background-color: ${({ backgroundColor }) => `${ds.color[backgroundColor]}`};
  margin-bottom: -20px;
  width: 100%;
  height: 370px;

  @media screen and (${ds.breakpoint.TABLET}) {
    height: 520px;
    overflow-x: hidden;
    margin: auto;
  }
`

const HidingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  left: 0;
  right: 0;
  position: absolute;
  width: 100%;
  height: 520px;
`
const HidingBlock = styled.div`
  width: calc(((100vw - 1360px) / 2) + 80px);
  background-color: ${ds.color.YELLOW};
  z-index: 3;
`

const InfiniteList = styled.ul`
  white-space: nowrap;
  overflow-y: hidden;
  overflow-x: hidden;
  position: absolute;
  left: 0;
  right: 0;
  width: 100vw;
  scroll-snap-type: x mandatory;

  z-index: 2;

  height: 370px;

  @media screen and (${ds.breakpoint.TABLET}) {
    height: 520px;
    overflow-x: hidden;
    margin: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`

const RotativeCardWrapper = styled.div`
  position: relative;
`
const ControlsWrapper = styled.div`
  position: relative;
  height: 100%;

  ${ds.CircularButton} {
    position: absolute;
    top: 45%;
    z-index: 5;

    @media not screen and (${ds.breakpoint.TABLET}) {
      width: 40px;
      height: 40px;
      min-height: 40px;
      top: 55%;

      ${ds.icons.Icon} {
        width: 24px;
        height: 24px;
        line-height: 24px;
      }
    }
  }

  ${ds.CircularButton}:nth-child(1) {
    left: 3%;
  }
  ${ds.CircularButton}:nth-child(2) {
    right: 3%;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${ds.CircularButton}:nth-child(1) {
      left: 1%;
    }
    ${ds.CircularButton}:nth-child(2) {
      right: 1%;
    }
  }

  ${Controls.HalfTransparentBlock} {
    z-index: 2;
    width: 15%;
  }
`

export const CriteriaSlider = styled(CriteriaSliderComponent)``
